import * as React from "react";
import { CSSProperties, ReactElement, ReactNode } from "react";
import styles from "./Badge.module.scss";

export type BadgeVariant = "green" | "gray" | "blue" | "red" | "yellow";

type BadgeProps = {
  /** Content shown inside the pill. */
  text: ReactNode;
  /** Preset color scheme. Add new presets in Badge.module.scss when a new
   * color is genuinely needed, rather than duplicating this component. */
  variant?: BadgeVariant;
  /** One-off color overrides, for cases a preset doesn't cover. Any of
   * these take precedence over the variant's own colors. */
  background?: string;
  color?: string;
  borderColor?: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * Badge
 * --------------------------------------------------------------------------
 * Reusable pill/badge: rounded, bold, small text, optional color preset.
 * Used for status/category chips (e.g. Owner Status, Account Type, FAQ
 * Category) so every screen doesn't roll its own badge CSS.
 *
 * Usage:
 *   import Badge from "Components/Badge/Badge";
 *   <Badge text="Owner" variant="green" />
 *   <Badge text="Custom" background="#f3e8ff" color="#7c3aed" borderColor="#d8b4fe" />
 */
const Badge = ({
  text,
  variant,
  background,
  color,
  borderColor,
  className,
  style,
}: BadgeProps): ReactElement => {
  const variantClass = variant ? styles[variant] : "";
  const classes = [styles.badge, variantClass, className].filter(Boolean).join(" ");

  const overrideStyle: CSSProperties = {
    ...(background ? { backgroundColor: background } : {}),
    ...(color ? { color } : {}),
    ...(borderColor ? { borderColor } : {}),
    ...style,
  };

  return (
    <span className={classes} style={overrideStyle}>
      {text}
    </span>
  );
};

export default Badge;
